import { Injectable } from "@angular/core";
import { CurrentUserService } from "@gtmhub/users";
import dayjs from "@webapp/shared/libs/dayjs";
import { ISessionTreeNode } from "../models/session-tree.model";
import { IPlanningSessionWithChildren, PlanningSessionStatus, Session } from "../models/sessions.model";

@Injectable({
  providedIn: "root",
})
export class SessionSelectorUtilsService {
  constructor(private currentUserService: CurrentUserService) {}

  public getSessionChildren(sessionItem: IPlanningSessionWithChildren, sessions: IPlanningSessionWithChildren[]): IPlanningSessionWithChildren[] {
    const children: IPlanningSessionWithChildren[] = [];

    const childrenIds = sessions
      .filter((s) => s.parentId === sessionItem.id)
      .sort(this.byEndDescAndStartAsc)
      .map((s) => s.id);

    if (!childrenIds.length) {
      return children;
    }

    childrenIds.forEach((childId) => {
      children.push(sessions.find((session) => session.id === childId));
    });

    return children;
  }

  private isSomeNodeInBranchAnOpenSession(session: IPlanningSessionWithChildren): boolean {
    if (session.status === PlanningSessionStatus.OPEN) {
      return true;
    }

    if (!session.children.length) {
      return false;
    }

    for (const child of session.children) {
      const isSomeNodeInChildBranchAnOpenSession = this.isSomeNodeInBranchAnOpenSession(child);

      if (isSomeNodeInChildBranchAnOpenSession) {
        return true;
      }
    }

    return false;
  }

  public byEndDescAndStartAsc(a: Session, b: Session): -1 | 0 | 1 {
    // "-end,start"
    const startA = dayjs(a.start);
    const startB = dayjs(b.start);
    const endA = dayjs(a.end);
    const endB = dayjs(b.end);

    if (endA.isSame(endB)) {
      return startA.isBefore(startB) ? -1 : startA.isAfter(startB) ? 1 : 0;
    } else {
      return endB.isBefore(endA) ? -1 : 1;
    }
  }

  public fillSessionWithPermissions(sessions: Session[]): Session[] {
    for (const session of sessions as IPlanningSessionWithChildren[]) {
      session.children = this.getSessionChildren(session, sessions as IPlanningSessionWithChildren[]);
    }

    for (const session of sessions as IPlanningSessionWithChildren[]) {
      if (session.status === PlanningSessionStatus.OPEN) {
        session.userHasPermissionToCreate = this.currentUserService.allowedActionsSync(session.access).includes("create");
      } else if (this.isSomeNodeInBranchAnOpenSession(session)) {
        session.userHasPermissionToCreate = false;
      } else {
        sessions = sessions.filter((s) => s !== session);
      }
    }

    return sessions;
  }

  public isAnyChild(parent: ISessionTreeNode, predicate: (child: ISessionTreeNode) => boolean): boolean {
    if (!parent.children || !parent.children.length) {
      return false;
    }

    return parent.children.some((child) => this.isAnyChild(child, predicate));
  }
}
